'use client'

import {captureException} from '@sentry/browser'
import {logger} from 'common'
import {CSSProperties, FC, useEffect} from 'react'

import {theme} from '@/theme/theme'

type GlobalErrorProps = {
  error: Error & {digest?: string}
  reset: () => void
}

const styles: Record<string, CSSProperties> = {
  body: {
    margin: 0,
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 24px',
    color: theme.palette.gray[900],
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    textAlign: 'center',
  },
  logo: {width: '120px', marginBottom: '40px'},
  title: {
    fontSize: '48px',
    lineHeight: '60px',
    letterSpacing: '-1px',
    margin: '0 0 16px',
  },
  description: {
    fontSize: '24px',
    lineHeight: '32px',
    margin: '0 0 32px',
    color: theme.palette.gray[600],
    maxWidth: '650px',
  },
  actions: {display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center'},
  buttonBase: {
    minHeight: '44px',
    padding: `${theme.spacing[2.5]} ${theme.spacing[3]}`,
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    fontWeight: theme.fontWeight.medium,
    textDecoration: 'none',
    minWidth: '175px',
  },
  button: {
    border: 'none',
    backgroundColor: theme.palette.button.primary.main,
    color: theme.palette.button.primary.contrastText,
  },
  link: {
    border: `1px solid ${theme.palette.button.tertiary.outlineColor}`,
    backgroundColor: theme.palette.button.tertiary.main,
    color: theme.palette.button.tertiary.contrastText,
  },
  digest: {marginTop: '32px', fontSize: '16px', lineHeight: '24px', color: theme.palette.gray[600]},
}

/**
 * Last-resort boundary: this renders only when the root layout itself fails,
 * so no providers, theme context or translations are available. It must supply
 * its own <html>/<body> and stay self-contained — in particular it must not
 * redirect to /500, which lives under the very layout that just threw.
 */
const GlobalError: FC<GlobalErrorProps> = ({error, reset}) => {
  useEffect(() => {
    logger.error(error, error.message)
    captureException(error)
  }, [error])

  return (
    <html lang="en">
      <body style={styles.body}>
        <div>
          <img src="/images/logo.svg" alt="Trama" style={styles.logo} />
          <h1 style={styles.title}>Something went wrong</h1>
          <p style={styles.description}>
            We hit an unexpected error and our team has been notified. Please try again in a moment.
          </p>
          <div style={styles.actions}>
            <button
              type="button"
              onClick={() => reset()}
              style={{...styles.buttonBase, ...styles.button}}
            >
              Try again
            </button>
            <a href="/" style={{...styles.buttonBase, ...styles.link}}>
              Go to homepage
            </a>
          </div>
          {error.digest ? <p style={styles.digest}>Reference: {error.digest}</p> : null}
        </div>
      </body>
    </html>
  )
}

export default GlobalError
